import { defineQuery, defineQueryOptions } from '@pinia/colada'
import type { ExcerptDetails, PaAPI, RefDataModel } from '@rialtic/types'
import { useSsrQueryCache } from '~/queries/query.helpers'
import { STALE_TIME } from '@rialtic/utils'

export * from './policies'

const CONTENT_DATA_QUERY_KEYS_ROOT = ['paapi', 'content-data'] as const
export const CONTENT_DATA_QUERY_KEYS = {
  root: CONTENT_DATA_QUERY_KEYS_ROOT,
  /** Edit types AKA Logic types */
  editTypes: [...CONTENT_DATA_QUERY_KEYS_ROOT, 'edit-types'] as const,

  /** Ref Data */
  refData: (modelName: RefDataModel) =>
    [...CONTENT_DATA_QUERY_KEYS_ROOT, 'ref-data', modelName] as const,

  refDataSearchPhrase: (modelName: RefDataModel, searchPhrase: string) =>
    [
      ...CONTENT_DATA_QUERY_KEYS_ROOT,
      'ref-data',
      modelName,
      'search_phrase',
      searchPhrase,
    ] as const,

  carcs: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'ref-data',
    'ReasonCodeMetadata',
    'carcs',
  ] as const,
  rarcs: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'ref-data',
    'ReasonCodeMetadata',
    'rarcs',
  ] as const,

  /** Engines */
  engines: [...CONTENT_DATA_QUERY_KEYS_ROOT, 'engines'] as const,
  engineCount: [...CONTENT_DATA_QUERY_KEYS_ROOT, 'engines', 'count'] as const,
  enginePolicyCount: (id: string) =>
    [...CONTENT_DATA_QUERY_KEYS_ROOT, 'engines', 'policy-count', id] as const,

  /** Reference Documents */
  referenceDocuments: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'reference-documents',
  ] as const,
  referenceDocumentById: (id: string) =>
    [...CONTENT_DATA_QUERY_KEYS_ROOT, 'reference-documents', id] as const,
  referenceDocumentDetailsById: (id: string) =>
    [
      ...CONTENT_DATA_QUERY_KEYS_ROOT,
      'reference-documents',
      id,
      'details',
    ] as const,
  referenceDocumentCodesById: (id: string) =>
    [
      ...CONTENT_DATA_QUERY_KEYS_ROOT,
      'reference-documents',
      id,
      'codes',
    ] as const,
  referenceDocumentDetails: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'reference-documents',
    'details',
  ] as const,
  referenceDocumentDetailsByType: (documentType: 'DOCUMENT' | 'DATASET') =>
    [
      ...CONTENT_DATA_QUERY_KEYS_ROOT,
      'reference-documents',
      'details',
      documentType,
    ] as const,
  referenceDocumentVersionsByRefDocId: (id: string) =>
    [
      ...CONTENT_DATA_QUERY_KEYS_ROOT,
      'reference-documents',
      id,
      'versions',
    ] as const,
  referenceDocumentVersionById: (id: string) =>
    [
      ...CONTENT_DATA_QUERY_KEYS_ROOT,
      'reference-document-version',
      id,
    ] as const,
  excerptsByVersionId: (id: string, deleted = false) =>
    [...CONTENT_DATA_QUERY_KEYS_ROOT, 'excerpts', id, deleted] as const,
  excerptByExcerptId: (excerptId: string) =>
    [...CONTENT_DATA_QUERY_KEYS_ROOT, 'excerpts', excerptId] as const,

  /** Source Documents */
  sourceDocuments: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-documents',
  ] as const,
  sourceDocumentById: (id: string) =>
    [...CONTENT_DATA_QUERY_KEYS_ROOT, 'source-documents', id] as const,
  sourceDocumentDetails: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-documents',
    'details',
  ] as const,

  /** Source types */
  sourceTypeLevelOnes: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-type-level-one',
  ] as const,
  sourceTypeLevelTwos: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-type-level-two',
  ],
  sourceTypeLevelTwosByLevelOneId: (id: string) => [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-type-level-two',
    id,
  ],
  sourceTypeRegions: [
    ...CONTENT_DATA_QUERY_KEYS_ROOT,
    'source-regions',
  ] as const,

  tags: [...CONTENT_DATA_QUERY_KEYS_ROOT, 'tags'] as const,

  /** Topics */
  topics: [...CONTENT_DATA_QUERY_KEYS_ROOT, 'topics'] as const,
}

/** Edit types AKA Logic types */
export const useEditTypes = defineQuery({
  key: CONTENT_DATA_QUERY_KEYS.editTypes,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/edit-type']>('/api/paapi/edit-type')
  },
  staleTime: STALE_TIME.ONE_DAY,
})

/** Ref Data */
export const refDataQuery = <
  T extends Partial<PaAPI.Get['/ref-data/{modelName}'][number]>,
>(
  modelName: RefDataModel,
) => {
  return {
    key: CONTENT_DATA_QUERY_KEYS.refData(modelName),
    enabled: Boolean(modelName),
    query: () => {
      const client = useRequestFetch()
      return client<T[]>(`/api/paapi/ref-data/${modelName}` as const)
    },
    staleTime: STALE_TIME.ONE_WEEK,
  }
}

export const refDataSearchPhraseQuery = defineQueryOptions(
  <TCode extends Partial<PaAPI.Get['/ref-data/{modelName}'][number]>>({
    modelName,
    searchPhrase,
  }: {
    modelName: RefDataModel
    searchPhrase: string
  }) => ({
    key: CONTENT_DATA_QUERY_KEYS.refDataSearchPhrase(modelName, searchPhrase),
    enabled: Boolean(modelName),
    query: () => {
      const client = useRequestFetch()
      return client<TCode[]>(
        `/api/paapi/ref-data/${modelName}/search` as const,
        {
          method: 'POST',
          body: {
            search_phrase: searchPhrase,
          },
        },
      )
    },
    staleTime: STALE_TIME.ONE_WEEK,
  }),
)

export const useDiagnosisCodes = defineQuery(
  refDataQuery<{
    code: string
    description: string
    effective_start: string
    effective_end: string
  }>('Diagnosis'),
)
export const useDiagnosisJCodes = defineQuery(
  refDataSearchPhraseQuery<{ code: string; description: string }>({
    modelName: 'Diagnosis',
    searchPhrase: 'J0000-J9999',
  }),
)
export const useDiagnosisQCodes = defineQuery(
  refDataSearchPhraseQuery<{ code: string; description: string }>({
    modelName: 'Diagnosis',
    searchPhrase: 'Q0000-Q9999',
  }),
)
export const useModifierCodes = defineQuery(refDataQuery('Modifier'))
export const usePlaceOfServiceCodes = defineQuery(
  refDataQuery('PlaceOfService'),
)
export const useProcedureCodes = defineQuery(
  refDataQuery<{ code: string; type: 'CPT' | 'HCPCS'; description: string }>(
    'Procedure',
  ),
)
export const useDrugJCodes = defineQuery(
  refDataSearchPhraseQuery<{
    code: string
    type: 'HCPCS'
    description: string
  }>({
    modelName: 'Procedure',
    searchPhrase: 'J0000-J9999,Q0000-Q9999',
  }),
)
const reasonCodeQuery = () =>
  refDataQuery<{ code: string; description: string; type: 'CARC' | 'RARC' }>(
    'ReasonCodeMetadata',
  )
export const useReasonCodes = () => useQuery(reasonCodeQuery())
export const useRevenueCodes = defineQuery(refDataQuery('Revenue'))

export interface SpecialtyCode {
  /** Not defined on object, but expected by PaAPI.Get['/ref-data/{modelName}']  */
  code: string
  id: string
  name: string
}

export interface ExcerptWithRelations extends ExcerptDetails {
  policy_excerpts?: { policy_id: string }[]
  excerpt_procedures?: { procedure_code: string }[]
  excerpt_specialties?: { specialty_id: string }[]
  excerpt_revenues?: { revenue_code: string }[]
  excerpt_type_of_bills?: { type_of_bill_code: string }[]
}
export const useSpecialtyCodes = defineQuery(
  refDataQuery<SpecialtyCode>('Specialty'),
)
export const useTypeOfBillCodes = defineQuery(refDataQuery('TypeOfBill'))

export const useCarcs = () =>
  useQuery({
    key: CONTENT_DATA_QUERY_KEYS.carcs,
    query: async () => {
      const queryCache = useSsrQueryCache()
      const { data } = await queryCache.refresh(
        queryCache.ensure(reasonCodeQuery()),
      )
      return data?.filter((code) => code.type === 'CARC') ?? []
    },
    staleTime: STALE_TIME.ONE_WEEK,
  })
export const useRarcs = () =>
  useQuery({
    key: CONTENT_DATA_QUERY_KEYS.rarcs,
    query: async () => {
      const queryCache = useSsrQueryCache()
      const { data } = await queryCache.refresh(
        queryCache.ensure(reasonCodeQuery()),
      )
      return data?.filter((code) => code.type === 'RARC') ?? []
    },
    staleTime: STALE_TIME.ONE_WEEK,
  })

/** Engine Queries */
export const useEngines = defineQuery({
  key: CONTENT_DATA_QUERY_KEYS.engines,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/engine']>('/api/paapi/engine')
  },
  staleTime: STALE_TIME.ONE_DAY,
})

export type EnginesListQuery = {
  limit?: number
  offset?: number
  search?: string
}

export const enginesListQuery = defineQueryOptions(
  (query: EnginesListQuery = {}) => ({
    key: [...CONTENT_DATA_QUERY_KEYS.engines, 'paginated', query] as const,
    query: () => {
      const client = useRequestFetch()
      return client<PaAPI.Get['/engine']>('/api/paapi/engine', { query })
    },
    staleTime: STALE_TIME.ONE_DAY,
  }),
)

export const engineByIdQuery = defineQueryOptions((id: string) => ({
  key: [...CONTENT_DATA_QUERY_KEYS.engines, 'byId', id] as const,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/engine/{id}']>(
      `/api/paapi/engine/${encodeURIComponent(id)}`,
    )
  },
  staleTime: STALE_TIME.ONE_DAY,
}))

export type EngineCountQuery = {
  search?: string
}

export const engineCountQuery = defineQueryOptions(
  (query: EngineCountQuery = {}) => ({
    key: [...CONTENT_DATA_QUERY_KEYS.engineCount, query] as const,
    query: () => {
      const client = useRequestFetch()
      return client<{ count: number }>('/api/paapi/engine/count', { query })
    },
    staleTime: STALE_TIME.ONE_DAY,
  }),
)

export const enginePolicyCountQuery = defineQueryOptions((id: string) => ({
  key: CONTENT_DATA_QUERY_KEYS.enginePolicyCount(id),
  query: () => {
    const client = useRequestFetch()
    return client<{ policyCount: number }>(
      `/api/paapi/engine/${encodeURIComponent(id)}/policy-count`,
    )
  },
  staleTime: STALE_TIME.ONE_DAY,
}))

type RefDoc = PaAPI.Schemas['ReferenceDocument']
type SourceTypeLevelOne = Prettify<
  Omit<
    PaAPI.Schemas['SourceTypeLevelOne'],
    'ExclusionVersionSourceType' | 'reference_document'
  >
>
type SourceTypeLevelTwo = Prettify<
  Omit<
    PaAPI.Schemas['SourceTypeLevelTwo'],
    'reference_document' | 'source_type_level_1'
  >
>
type Region = Omit<
  PaAPI.Schemas['Region'],
  'connector' | 'reference_document_region'
>

export type ExpandedReferenceDocument = Prettify<
  Omit<
    PaAPI.Get['/reference-document'][number],
    'source_type_level_1' | 'source_type_level_2' | 'reference_document_region'
  > & {
    source_type_level_1: SourceTypeLevelOne | null
    source_type_level_2: SourceTypeLevelTwo | null
    reference_document_region: (RefDoc['reference_document_region'][number] & {
      region?: Region
    })[]
  }
>
/** Reference Document Queries */
export const referenceDocumentsQuery = defineQueryOptions({
  key: CONTENT_DATA_QUERY_KEYS.referenceDocuments,
  query: async (): Promise<ExpandedReferenceDocument[]> => {
    const queryCache = useSsrQueryCache()
    const [
      { data: sourceTypeLevelOneData },
      { data: sourceTypeLevelTwoData },
      { data: sourceTypeRegionData },
    ] = await Promise.all([
      queryCache.refresh(queryCache.ensure(sourceTypeLevelOneQuery)),
      queryCache.refresh(queryCache.ensure(sourceTypeLevelTwoQuery)),
      queryCache.refresh(queryCache.ensure(sourceTypeRegionQuery)),
    ])

    const client = useRequestFetch()
    const docs = await client<PaAPI.Get['/reference-document']>(
      '/api/paapi/reference-document',
    )
    const sourceTypeLevelOnes: Record<string, SourceTypeLevelOne> =
      sourceTypeLevelOneData?.reduce<Record<string, SourceTypeLevelOne>>(
        (acc, sourceTypeLevelOne: SourceTypeLevelOne) => {
          acc[sourceTypeLevelOne.id] = sourceTypeLevelOne
          return acc
        },
        {},
      ) ?? {}

    const sourceTypeLevelTwos: Record<string, SourceTypeLevelTwo> =
      sourceTypeLevelTwoData?.reduce<Record<string, SourceTypeLevelTwo>>(
        (acc, sourceTypeLevelTwo: SourceTypeLevelTwo) => {
          acc[sourceTypeLevelTwo.id] = sourceTypeLevelTwo
          return acc
        },
        {},
      ) ?? {}

    const sourceTypeRegions: Record<string, Region> =
      sourceTypeRegionData?.reduce<Record<string, Region>>(
        (acc, sourceTypeRegion: Region) => {
          acc[sourceTypeRegion.id] = sourceTypeRegion
          return acc
        },
        {},
      ) ?? {}

    return docs.map(
      (doc): ExpandedReferenceDocument => ({
        ...doc,
        source_type_level_1:
          doc.source_type_level_1_id &&
          sourceTypeLevelOnes[doc.source_type_level_1_id]
            ? (sourceTypeLevelOnes[
                doc.source_type_level_1_id
              ] as SourceTypeLevelOne)
            : null,
        source_type_level_2:
          doc.source_type_level_2_id &&
          sourceTypeLevelTwos[doc.source_type_level_2_id]
            ? (sourceTypeLevelTwos[
                doc.source_type_level_2_id
              ] as SourceTypeLevelTwo)
            : null,
        reference_document_region: doc.reference_document_region.map(
          (region) => ({
            ...region,
            region: sourceTypeRegions[region.region_id],
          }),
        ),
      }),
    )
  },
  staleTime: STALE_TIME.FIFTEEN_MINUTES,
})
export const useReferenceDocuments = defineQuery(referenceDocumentsQuery)

export type ReferenceDocumentDetails = {
  id: string
  name: string
  status: string
  document_type: string
  source_type_level_1: string | null
  source_type_level_2: string | null
  version_count: number
  excerpt_count: number
  policy_count: number
  updated_at: string | null
  retired_date: string | null
  is_hidden: boolean
}

export type PaginatedReferenceDocumentDetails = {
  items: ReferenceDocumentDetails[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

export type ReferenceDocumentDetailsQuery = {
  page?: number
  pageSize?: number
  search?: string
  document_type?: 'DOCUMENT' | 'DATASET'
  status?: string
}

const fetchReferenceDocumentDetails = (
  query: ReferenceDocumentDetailsQuery = {},
): Promise<PaginatedReferenceDocumentDetails> => {
  const client = useRequestFetch()
  return client<PaginatedReferenceDocumentDetails>(
    '/api/paapi/reference-document/details',
    { query },
  )
}

export const referenceDocumentDetailsQuery = defineQueryOptions(
  (query: ReferenceDocumentDetailsQuery = {}) => ({
    key: [...CONTENT_DATA_QUERY_KEYS.referenceDocumentDetails, query] as const,
    query: () => fetchReferenceDocumentDetails(query),
    staleTime: STALE_TIME.FIFTEEN_MINUTES,
  }),
)

export const referenceDocumentDetailsByType = defineQueryOptions(
  (documentType: 'DOCUMENT' | 'DATASET') => ({
    key: CONTENT_DATA_QUERY_KEYS.referenceDocumentDetailsByType(documentType),
    query: () => fetchReferenceDocumentDetails({ document_type: documentType }),
    staleTime: STALE_TIME.FIFTEEN_MINUTES,
  }),
)

export type ReferenceDocumentDetailsById = Omit<
  PaAPI.Get['/reference-document/{id}/details'],
  'policies'
> & {
  policies: ReferenceDocumentDetailsPolicy[]
}

export type ReferenceDocumentDetailsPolicy = {
  id: string
  name: string
  latest_review_status: string | null
  relation_source: PaAPI.Get['/reference-document/{id}/details']['policies'][number]['relation_source']
  is_primary: boolean
}

export const referenceDocumentDetailsById = defineQueryOptions((id: string) => {
  return {
    key: CONTENT_DATA_QUERY_KEYS.referenceDocumentDetailsById(id),
    enabled: Boolean(id),
    query: async () => {
      const client = useRequestFetch()

      try {
        return await client<ReferenceDocumentDetailsById>(
          `/api/paapi/reference-document/${id}/details` as const,
        )
      } catch {
        return client<ReferenceDocumentDetailsById>(
          `/reference-document/${id}/details` as const,
        )
      }
    },
    staleTime: STALE_TIME.FIFTEEN_MINUTES,
  }
})

export const referenceDocumentCodesById = defineQueryOptions((id: string) => {
  return {
    key: CONTENT_DATA_QUERY_KEYS.referenceDocumentCodesById(id),
    enabled: Boolean(id),
    query: async () => {
      const client = useRequestFetch()

      try {
        return await client<PaAPI.Get['/reference-document/{id}/codes']>(
          `/api/paapi/reference-document/${id}/codes` as const,
        )
      } catch {
        return client<PaAPI.Get['/reference-document/{id}/codes']>(
          `/reference-document/${id}/codes` as const,
        )
      }
    },
    staleTime: STALE_TIME.FIFTEEN_MINUTES,
  }
})

export const referenceDocumentById = defineQueryOptions((id: string) => {
  return {
    key: CONTENT_DATA_QUERY_KEYS.referenceDocumentById(id),
    enabled: Boolean(id),
    query: () => {
      const client = useRequestFetch()
      return client<PaAPI.Get['/reference-document/{id}']>(
        `/api/paapi/reference-document/${id}` as const,
      )
    },
    staleTime: STALE_TIME.FIFTEEN_MINUTES,
  }
})

export const referenceDocumentVersionsByRefDocId = defineQueryOptions(
  (id: string) => {
    return {
      key: CONTENT_DATA_QUERY_KEYS.referenceDocumentVersionsByRefDocId(id),
      enabled: Boolean(id),
      query: async () => {
        const queryCache = useSsrQueryCache()
        await queryCache.ensure(sourceDocumentsQuery)
        const client = useRequestFetch()
        const versions = await client<
          PaAPI.Schemas['ReferenceDocumentVersion'][]
        >(
          `/api/paapi/reference-document-version?ref_doc_id=${id}&result=all` as const,
        )
        return versions.toSorted((a, b) => {
          const aTimestamp = new Date(a.effective_date_start ?? '').getTime()
          const bTimestamp = new Date(b.effective_date_start ?? '').getTime()

          return bTimestamp - aTimestamp
        })
      },
      staleTime: STALE_TIME.FIFTEEN_MINUTES,
    }
  },
)

export const referenceDocumentVersionById = defineQueryOptions((id: string) => {
  return {
    key: CONTENT_DATA_QUERY_KEYS.referenceDocumentVersionById(id),
    enabled: Boolean(id),
    query: async () => {
      const client = useRequestFetch()
      return client<PaAPI.Get['/reference-document-version/{id}']>(
        `/api/paapi/reference-document-version/${id}` as const,
        {
          query: {
            include: true,
          },
        },
      )
    },
  }
})

export const excerptsByVersionId = defineQueryOptions((id: string) => {
  return {
    key: CONTENT_DATA_QUERY_KEYS.excerptsByVersionId(id),
    enabled: Boolean(id),
    query: async () => {
      const client = useRequestFetch()
      return client<PaAPI.Get['/reference-document-version/{id}/excerpt']>(
        `/api/paapi/reference-document-version/${id}/excerpt`,
        {
          query: {
            include: true,
          },
        },
      )
    },
  }
})

export const excerptByExcerptId = defineQueryOptions((excerptId: string) => {
  return {
    key: CONTENT_DATA_QUERY_KEYS.excerptByExcerptId(excerptId),
    enabled: Boolean(excerptId),
    query: async () => {
      const client = useRequestFetch()
      return client<ExcerptDetails>(`/api/paapi/excerpt/${excerptId}`, {
        query: {
          include: true,
        },
      })
    },
  }
})

/** Source Document Queries */
const sourceDocumentsQuery = defineQueryOptions({
  key: CONTENT_DATA_QUERY_KEYS.sourceDocuments,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/source-document']>('/api/paapi/source-document')
  },
  staleTime: STALE_TIME.FIFTEEN_MINUTES,
})
export const useSourceDocuments = defineQuery(sourceDocumentsQuery)

export const sourceDocumentById = defineQueryOptions((id: string) => {
  return {
    key: CONTENT_DATA_QUERY_KEYS.sourceDocumentById(id),
    enabled: Boolean(id),
    query: () => {
      const client = useRequestFetch()
      return client<PaAPI.Get['/source-document/{id}']>(
        `/api/paapi/source-document/${id}` as const,
      )
    },
    staleTime: STALE_TIME.FIFTEEN_MINUTES,
  }
})

export type PaginatedSourceDocumentDetails =
  PaAPI.Get['/source-document/details']

export type SourceDocumentDetail =
  PaginatedSourceDocumentDetails['items'][number]

export type SourceDocumentDetailsQuery = {
  page?: number
  pageSize?: number
  search?: string
  format?: string
}

export const sourceDocumentDetailsQuery = defineQueryOptions(
  (query: SourceDocumentDetailsQuery = {}) => ({
    key: [...CONTENT_DATA_QUERY_KEYS.sourceDocumentDetails, query] as const,
    query: () => {
      const client = useRequestFetch()
      return client<PaginatedSourceDocumentDetails>(
        '/api/paapi/source-document/details',
        { query },
      )
    },
    staleTime: STALE_TIME.FIFTEEN_MINUTES,
  }),
)

/** Source Type Queries */
const sourceTypeLevelOneQuery = defineQueryOptions({
  key: CONTENT_DATA_QUERY_KEYS.sourceTypeLevelOnes,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/source-type-level-one']>(
      '/api/paapi/source-type-level-one',
    )
  },
  staleTime: STALE_TIME.ONE_DAY,
})
export const useSourceTypeLevelOnes = defineQuery(sourceTypeLevelOneQuery)

const sourceTypeLevelTwoQuery = defineQueryOptions({
  key: CONTENT_DATA_QUERY_KEYS.sourceTypeLevelTwos,
  query: async () => {
    const queryCache = useSsrQueryCache()
    const client = useRequestFetch()
    const [{ data }, levelTwos] = await Promise.all([
      queryCache.refresh(queryCache.ensure(sourceTypeLevelOneQuery)),
      client<PaAPI.Get['/source-type-level-two']>(
        '/api/paapi/source-type-level-two',
      ),
    ])

    return levelTwos.map((levelTwo) => {
      const levelOne = data?.find(
        (levelOne) => levelOne.id === levelTwo.source_type_level_1_id,
      )
      return { ...levelTwo, source_type_level_1: levelOne }
    })
  },
  staleTime: STALE_TIME.ONE_DAY,
})

export const useSourceTypeLevelTwos = defineQuery(sourceTypeLevelTwoQuery)

export const sourceTypeLevelTwosByLevelOneId = defineQueryOptions(
  (id: string) => {
    return {
      key: CONTENT_DATA_QUERY_KEYS.sourceTypeLevelTwosByLevelOneId(id),
      enabled: Boolean(id),
      query: async () => {
        const queryCache = useSsrQueryCache()
        const { data } = await queryCache.refresh(
          queryCache.ensure(sourceTypeLevelTwoQuery),
        )

        const levelTwos = data?.filter(
          (item) => item.source_type_level_1_id === id,
        )

        return levelTwos
      },
      staleTime: STALE_TIME.ONE_DAY,
    }
  },
)

const sourceTypeRegionQuery = defineQueryOptions({
  key: CONTENT_DATA_QUERY_KEYS.sourceTypeRegions,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/region']>('/api/paapi/region')
  },
  staleTime: STALE_TIME.ONE_DAY,
})
export const useSourceTypeRegions = defineQuery(sourceTypeRegionQuery)

/** Tag Queries */
export const useTags = defineQuery({
  key: CONTENT_DATA_QUERY_KEYS.tags,
  query: async () => {
    const client = useRequestFetch()
    const tags = await client<{ id: string; name: string }[]>('/api/paapi/tags')
    return tags ?? []
  },
  staleTime: STALE_TIME.ONE_DAY,
})

/** Topic Queries */
export const useTopics = defineQuery({
  key: CONTENT_DATA_QUERY_KEYS.topics,
  query: () => {
    const client = useRequestFetch()
    return client<PaAPI.Get['/topic']>('/api/paapi/topic')
  },
  staleTime: STALE_TIME.ONE_DAY,
})
